import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export interface ProfileSectionAnchorProps {
  /**
   * Stable identifier for this section. Surfaced as both the
   * `data-section` attribute (read by the IntersectionObserver inside
   * `useProfileSectionObserver`) and a hook the caller can use to drive
   * `scrollToSection`.
   */
  id: string;
  /**
   * React 19 callback ref returned by
   * `useProfileSectionObserver(...).registerSectionRef(id)`. The ref both
   * registers the element with the observer on mount and deregisters on
   * unmount via the cleanup function React 19 lets callback refs return.
   *
   * The anchor itself does NOT call the hook so that callers can hoist
   * `useProfileSectionObserver` once and distribute callback refs across
   * the section anchors they render.
   */
  ref?: (el: HTMLElement | null) => void | (() => void);
  /** Section content. */
  children: ReactNode;
  /**
   * Additional className appended to the canonical
   * `"scroll-mt-20"` (provides space below the sticky tab bar so the
   * `scrollIntoView` target does not land flush against the top).
   */
  className?: string;
}

/**
 * Profile section anchor primitive used by every profile entry point that
 * supports anchor-tab scrolling. Renders a single `<div>` wrapping the
 * section content with:
 *
 *   - the canonical `"scroll-mt-20"` className (sticky tab bar offset),
 *   - a `data-section={id}` attribute the section observer reads inside its
 *     IntersectionObserver callback,
 *   - the caller-supplied callback ref so the observer can register the
 *     element on mount and deregister on unmount.
 *
 * This component does NOT call `useProfileSectionObserver`. Callers hoist
 * the hook once and distribute callback refs:
 *
 *   <Profile.SectionAnchor id="experience" ref={registerSectionRef("experience")}>
 *     <Profile.Section.Experience ... />
 *   </Profile.SectionAnchor>
 */
export function ProfileSectionAnchor({
  id,
  ref,
  children,
  className,
}: ProfileSectionAnchorProps) {
  return (
    <div
      ref={ref}
      data-section={id}
      className={cn("scroll-mt-20", className)}
    >
      {children}
    </div>
  );
}
